import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Alert,
  Link,
  Grid,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Login as LoginIcon
} from '@mui/icons-material';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getCurrentOrganization, buildOrgUrl } from '../utils/organizationContext';
import axios from 'axios';


const Login = () => {
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { login, organizationSlug } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Get organization from URL
  // Don't extract organization slug if we're on /admin/login (super-admin route)
  const isAdminLoginRoute = window.location.pathname === '/admin/login';
  const orgSlug = isAdminLoginRoute ? null : (params.organizationSlug || organizationSlug || getCurrentOrganization());
  
  // Check if we have an error message from redirection
  useEffect(() => {
    if (location.state?.message) {
      setError(location.state.message);
    }
    
    // Check URL for expired=true parameter
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('expired') === 'true') {
      setError('Your session has expired. Please log in again.');
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Login attempt started with credentials:', { email: credentials.email, password: '***' });
    console.log('API URL:', process.env.REACT_APP_API_URL);
    console.log('Organization slug from URL:', orgSlug);
    
    try {
      setLoading(true);
      console.log('Making POST request to:', `${process.env.REACT_APP_API_URL}/api/auth/login`);
      
      // Include organization_slug in login request if available
      const loginPayload = {
        ...credentials,
        ...(orgSlug && { organization_slug: orgSlug })
      };
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/login`,
        loginPayload
      );
      
      console.log('Login response received:', response.data);
      const { token, user } = response.data;
      
      // Use the login function from AuthContext
      login(token, user);
      
      // Check if this is admin login (no organization context)
      const isAdminLogin = window.location.pathname === '/admin/login';
      
      // Redirect based on user role and context
      if (isAdminLogin) {
        if (user.role === 'system_admin' && !user.organization_id) {
          navigate('/master-dashboard', { replace: true });
        } else if (user.organization_id && user.organization_slug) {
          navigate(`/${user.organization_slug}/dashboard`, { replace: true });
        } else if (user.role === 'system_admin' && user.organization_id) {
          navigate('/admin/dashboard', { replace: true });
        } else {
          navigate('/admin/dashboard', { replace: true });
        }
      } else if (user.role === 'unit_member') {
        const slug = orgSlug || user.organization_slug;
        if (slug) {
          navigate(buildOrgUrl('/my-tasks', slug), { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      } else if (user.role === 'unit_manager') {
        if (user.organization_slug) {
          navigate(`/${user.organization_slug}/dashboard`, { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      } else if (user.role === 'system_admin' && user.organization_id) {
        navigate('/admin/dashboard', { replace: true });
      } else if (user.organization_slug) {
        navigate(`/${user.organization_slug}/dashboard`, { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response);
      setError(error.response?.data?.error || 'An error occurred during login');
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#111827',
        backgroundImage: 'linear-gradient(rgba(10, 17, 30, 0.48), rgba(10, 17, 30, 0.58)), url("/assets/prayer-monochrome.webp")',
        backgroundSize: 'cover',
        backgroundPosition: { xs: '38% center', md: 'center center' },
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        alignItems: 'center',
        py: 4
      }}
    >
      <Container
        maxWidth="lg"
        sx={{ display: 'flex', justifyContent: 'center' }}
      >
        <Paper 
          elevation={8}
          sx={{ 
            width: '100%',
            maxWidth: 520,
            p: { xs: 3, sm: 6 },
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.97)',
            border: '1px solid rgba(255, 255, 255, 0.72)',
            boxShadow: '0 24px 64px rgba(10, 17, 30, 0.34)'
          }}
        >
          {/* Header */}
          <Box textAlign="center" mb={4}>
            <img
              src="/assets/church-logo.png"
              alt="Winners Chapel Logo"
              style={{
                height: '60px',
                width: '60px',
                marginBottom: '16px',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                borderRadius: '50%',
                objectFit: 'cover'
              }}
            />
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #DC2626 0%, #991B1B 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1
              }}
            >
              Welcome Back
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Sign in to your Winners Chapel International Canada Follow-Up account
            </Typography>
          </Box>
          
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: 2,
                '& .MuiAlert-icon': {
                  alignItems: 'center'
                }
              }}
            >
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              margin="normal"
              label="Email Address"
              type="email"
              value={credentials.email}
              onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                },
              }}
            />
            
            <TextField
              fullWidth
              margin="normal"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={credentials.password}
              onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                },
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
              sx={{ 
                mt: 4, 
                mb: 3,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #DC2626 0%, #991B1B 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #B91C1C 0%, #7F1D1D 100%)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 8px 25px rgba(220, 38, 38, 0.3)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>
            
            <Divider sx={{ my: 3 }}>
              <Typography variant="body2" color="text.secondary">
                or
              </Typography>
            </Divider>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => navigate('/forgot-password')}
                  sx={{
                    py: 1.5,
                    borderColor: theme.palette.grey[300],
                    color: theme.palette.text.secondary,
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  Forgot Password?
                </Button>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    const signupPath = orgSlug ? `/${orgSlug}/signup` : '/signup';
                    navigate(signupPath);
                  }}
                  sx={{
                    py: 1.5,
                    borderColor: theme.palette.grey[300],
                    color: theme.palette.text.secondary,
                    '&:hover': {
                      borderColor: theme.palette.secondary.main,
                      color: theme.palette.secondary.main,
                    },
                  }}
                >
                  Create Account
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
